// School admin: the review queue for every report from the school's practice sessions,
// across all classes. Safety concerns come first, then newest. Teachers see their own
// class's reports on the class page (ClassReportsSection); this is the school-wide view.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import {
  Page,
  ReportStatus,
  SAFETY_PAGE_SIZE,
  SafetyReport,
  fetchSafetyQueue,
  reviewSafetyReport,
} from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  linkButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
} from './classesStyles';

const logger = createLogger('SafetyQueueTab');

const FILTERS: Array<ReportStatus | 'all'> = ['open', 'resolved', 'dismissed', 'all'];

interface Props {
  organizationId?: string | null;
}

const SafetyQueueTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<Page<SafetyReport> | null>(null);
  const [status, setStatus] = useState<ReportStatus | 'all'>('open');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const loaded = await fetchSafetyQueue({ status, page }, organizationId);
      const lastPage = Math.max(1, Math.ceil(loaded.total / SAFETY_PAGE_SIZE));
      if (loaded.items.length === 0 && page > lastPage) {
        // The last item on the last page was resolved away: go to the page that now is
        // last (this effect reloads it) rather than strand the admin on an empty one.
        setPage(lastPage);
        return;
      }
      setQueue(loaded);
    } catch (err) {
      logger.error('Failed to load the safety queue', err);
      setError(errorMessage(err, t('studentAdmin.safety.couldNotLoad')));
    }
  }, [organizationId, status, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (report: SafetyReport, next: ReportStatus) => {
    setBusy(report.id);
    setError(null);
    try {
      await reviewSafetyReport(report.id, next, organizationId);
      // Reload rather than patch in place: the report may leave this filter, and the
      // retention hold on its class can change with it.
      await load();
    } catch (err) {
      logger.error('Failed to update the report', err);
      setError(errorMessage(err, t('classReports.couldNotUpdate')));
    } finally {
      setBusy(null);
    }
  };

  const totalPages = queue ? Math.max(1, Math.ceil(queue.total / SAFETY_PAGE_SIZE)) : 1;

  return (
    <section style={cardStyle} aria-labelledby="safety-queue-heading">
      <h2 id="safety-queue-heading" style={sectionHeadingStyle}>
        {t('studentAdmin.safety.title')}
      </h2>
      <p style={{ ...hintStyle, marginTop: 0 }}>{t('studentAdmin.safety.hint')}</p>
      <select
        aria-label={t('common.status')}
        style={{ ...inputStyle, margin: '1rem 0' }}
        value={status}
        onChange={e => {
          setStatus(e.target.value as ReportStatus | 'all');
          setPage(1);
        }}
      >
        {FILTERS.map(f => (
          <option key={f} value={f}>
            {f === 'all' ? t('common.all') : t(`classReports.statuses.${f}`)}
          </option>
        ))}
      </select>

      {error && <div style={errorStyle} role="alert">{error}</div>}

      {!queue ? (
        !error && <LoadingIndicator message={t('studentAdmin.safety.loading')} />
      ) : queue.items.length === 0 ? (
        <p style={hintStyle}>{t('studentAdmin.safety.none')}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {queue.items.map(report => (
            <li key={report.id} data-testid="safety-report" style={{ borderTop: '1px solid #e2e8f0', padding: '0.75rem 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.9rem' }}>
                  {t(`classReports.types.${report.report_type}`)}
                  <span style={{ fontWeight: 400, color: '#64748b' }}>
                    {' · '}
                    {report.class_name || t('studentAdmin.safety.noClass')}
                    {' · '}
                    {report.reported_by === 'system'
                      ? t('classReports.byPlatform')
                      : report.reporter_name || t('classReports.byStudent')}
                    {' · '}
                    {new Date(report.created_at).toLocaleString(uiLocale())}
                  </span>
                </strong>
                <span style={statusBadgeStyle(report.status !== 'open')}>{t(`classReports.statuses.${report.status}`)}</span>
              </div>
              {report.message_text && <q style={{ display: 'block', fontSize: '0.85rem', margin: '6px 0' }}>{report.message_text}</q>}
              {report.details && <p style={{ ...hintStyle, margin: '4px 0' }}>{report.details}</p>}
              {report.holds_class_deletion && (
                <p style={{ ...hintStyle, margin: '4px 0', color: '#92400e' }}>{t('studentAdmin.safety.holdsDeletion')}</p>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                <Link to={`/sessions/${report.roleplay_session_id}`} style={{ ...linkButtonStyle, textDecoration: 'none' }}>
                  {t('classReports.openSession')}
                </Link>
                {report.status === 'open' ? (
                  <>
                    <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => review(report, 'resolved')}>
                      {t('classReports.resolve')}
                    </button>
                    <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => review(report, 'dismissed')}>
                      {t('classReports.dismiss')}
                    </button>
                  </>
                ) : (
                  <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => review(report, 'open')}>
                    {t('classReports.reopen')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: '1rem' }}>
          <button type="button" style={secondaryButtonStyle} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            {t('classes.previousPage')}
          </button>
          <span style={{ fontSize: '0.85rem' }}>{t('classes.pageOf', { page, total: totalPages })}</span>
          <button type="button" style={secondaryButtonStyle} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            {t('classes.nextPage')}
          </button>
        </div>
      )}
    </section>
  );
};

export default SafetyQueueTab;
